import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Banknote,
  X,
  Check,
  Loader2,
  ArrowLeft,
  User,
  ShoppingCart,
  Scan,
  Calculator,
  Printer,
  Split,
  ParkingCircle,
  RotateCcw,
  WifiOff,
  Wifi,
  CloudOff,
  RefreshCw,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import ShopLayout from '@/components/shop/ShopLayout';
import ReceiptPrint from '@/components/shop/ReceiptPrint';
import CameraBarcodeScanner from '@/components/shop/CameraBarcodeScanner';
import {
  cacheProducts,
  getCachedProducts,
  cacheShop,
  getCachedShop,
  savePendingSale,
  getPendingSales,
  getSyncMeta
} from '@/services/offlineDB';
import { syncPendingSales, getPendingCount } from '@/services/syncService';

const POS = () => {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const barcodeBuffer = useRef('');
  const barcodeTimeout = useRef(null);
  const { isOnline, wasOffline, onReconnect } = useOnlineStatus();

  // State
  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [saleComplete, setSaleComplete] = useState(false);
  const [lastSale, setLastSale] = useState(null);

  // Payment state
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [customerName, setCustomerName] = useState('');

  // Offline state
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [usingCache, setUsingCache] = useState(false);

  // Load data (with offline fallback)
  useEffect(() => {
    fetchData();
  }, []);

  // Auto-sync when reconnecting
  useEffect(() => {
    const unsub = onReconnect(() => {
      handleSync();
      // Refresh products from server
      fetchData();
    });
    return unsub;
  }, [onReconnect]);

  // Update pending count periodically
  useEffect(() => {
    const updatePending = async () => {
      const count = await getPendingCount();
      setPendingCount(count);
    };
    updatePending();
    const interval = setInterval(updatePending, 5000);
    return () => clearInterval(interval);
  }, []);

  // Barcode scanner listener (USB scanners send keystrokes)
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (document.activeElement.tagName === 'INPUT') return;
      if (e.key === 'Enter' && barcodeBuffer.current.length > 3) {
        handleBarcodeScanned(barcodeBuffer.current);
        barcodeBuffer.current = '';
        return;
      }
      if (e.key.length === 1) {
        barcodeBuffer.current += e.key;
        clearTimeout(barcodeTimeout.current);
        barcodeTimeout.current = setTimeout(() => {
          barcodeBuffer.current = '';
        }, 100);
      }
    };
    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [products]);

  const fetchData = async () => {
    setLoading(true);
    setUsingCache(false);

    if (isOnline) {
      try {
        const shopsResponse = await api.get('/shops');
        if (shopsResponse.data.length === 0) {
          navigate('/shop/setup');
          return;
        }
        const shopData = shopsResponse.data[0];
        setShop(shopData);
        await cacheShop(shopData);

        const productsResponse = await api.get(`/shops/${shopData.id}/products`);
        setProducts(productsResponse.data);
        await cacheProducts(shopData.id, productsResponse.data);

        const cats = [...new Set(productsResponse.data.map(p => p.category))];
        setCategories(cats);

        // Update pending count
        const count = await getPendingCount();
        setPendingCount(count);
      } catch (error) {
        console.error('Failed to fetch POS data online, trying cache:', error);
        await loadFromCache();
      }
    } else {
      await loadFromCache();
    }
    setLoading(false);
  };

  const loadFromCache = async () => {
    const cachedShop = await getCachedShop();
    if (cachedShop) {
      setShop(cachedShop);
      const cachedProducts = await getCachedProducts(cachedShop.id);
      setProducts(cachedProducts);
      const cats = [...new Set(cachedProducts.map(p => p.category))];
      setCategories(cats);
      setUsingCache(true);

      const count = await getPendingCount();
      setPendingCount(count);
    }
  };

  const handleBarcodeScanned = (barcode) => {
    const product = products.find(p => p.barcode === barcode);
    if (product) addToCart(product);
  };

  const handleCameraScan = (barcode) => {
    setShowCameraScanner(false);
    handleBarcodeScanned(barcode);
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev =>
      prev.map(item => {
        if (item.id === productId) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean)
    );
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setCustomerName('');
    setCashReceived('');
    setPaymentMethod('cash');
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let taxTotal = 0;
    cart.forEach(item => {
      const itemTotal = item.price * item.quantity;
      const taxAmount = itemTotal * (item.tax_rate / 100);
      subtotal += itemTotal;
      taxTotal += taxAmount;
    });
    return { subtotal, taxTotal, total: subtotal + taxTotal };
  };

  const { subtotal, taxTotal, total } = calculateTotals();

  const handlePayment = async () => {
    if (cart.length === 0) return;
    setProcessing(true);

    const saleData = {
      shop_id: shop.id,
      items: cart.map(item => ({
        product_id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        tax_rate: item.tax_rate,
      })),
      payment_method: paymentMethod,
      subtotal,
      tax_total: taxTotal,
      total,
      cash_received: paymentMethod === 'cash' ? parseFloat(cashReceived) || total : null,
      change_given: paymentMethod === 'cash' ? Math.max(0, (parseFloat(cashReceived) || total) - total) : null,
      customer_name: customerName || null,
    };

    if (isOnline) {
      try {
        const response = await api.post(`/shops/${shop.id}/sales`, saleData);
        setLastSale(response.data);
        setSaleComplete(true);
        setShowPayment(false);
      } catch (error) {
        // If API fails while "online" (flaky connection), save offline
        console.error('Sale API failed, saving offline:', error);
        await saveOfflineSale(saleData);
      }
    } else {
      await saveOfflineSale(saleData);
    }
    setProcessing(false);
  };

  const saveOfflineSale = async (saleData) => {
    const pendingSale = await savePendingSale(saleData);
    setLastSale({
      sale_number: `OFFLINE-${pendingSale.offline_id.slice(-6).toUpperCase()}`,
      total: saleData.total,
      payment_method: saleData.payment_method,
      cash_received: saleData.cash_received,
      change_given: saleData.change_given,
      items: saleData.items,
      created_at: pendingSale.created_at,
      is_offline: true,
    });
    setSaleComplete(true);
    setShowPayment(false);
    const count = await getPendingCount();
    setPendingCount(count);
  };

  const handleSync = async () => {
    if (syncing || !isOnline) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await syncPendingSales(api);
      setSyncResult(result);
      const count = await getPendingCount();
      setPendingCount(count);
      // Clear result after 5s
      setTimeout(() => setSyncResult(null), 5000);
    } catch (err) {
      console.error('Sync error:', err);
      setSyncResult({ synced: 0, failed: 1, errors: [err.message] });
      setTimeout(() => setSyncResult(null), 5000);
    }
    setSyncing(false);
  };

  const handleNewSale = () => {
    clearCart();
    setSaleComplete(false);
    setLastSale(null);
  };

  const filteredProducts = products.filter(p => {
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    const matchesSearch = !searchQuery ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.barcode?.includes(searchQuery);
    return matchesCategory && matchesSearch;
  });

  const quickCashAmounts = [5, 10, 20, 50];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  // Sale Complete Screen - shown as overlay within the POS area
  if (saleComplete && lastSale) {
    const fi = lastSale.fiscal_invoice;
    return (
      <ShopLayout>
        <div className="flex items-center justify-center py-8 px-4" data-testid="sale-complete-screen">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white border-2 border-[#22c55e] rounded-lg p-8 max-w-md w-full text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-10 h-10 text-[#22c55e]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">{t('pos_sale_success')}</h2>
            <p className="text-slate-600 mb-1">{t('pos_sale_number')}{lastSale.sale_number}</p>

            {fi && (
              <p className="text-xs text-slate-400 font-mono mb-4">
                Fiscal: {fi.invoice_series}-{String(fi.invoice_number).padStart(5, '0')}
              </p>
            )}

            {lastSale.is_offline && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-2" data-testid="offline-sale-badge">
                <CloudOff className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-700">{t('pos_offline_saved')}</p>
              </div>
            )}

            <div className="bg-slate-50 p-4 rounded-lg mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-slate-600">{t('pos_total')}</span>
                <span className="text-2xl font-bold text-[#22c55e]">€{lastSale.total.toFixed(2)}</span>
              </div>
              {lastSale.payment_method === 'cash' && lastSale.change_given > 0 && (
                <div className="flex justify-between text-lg">
                  <span className="text-slate-600">{t('pos_change')}</span>
                  <span className="font-bold text-[#1e3a5f]">€{lastSale.change_given.toFixed(2)}</span>
                </div>
              )}
              {fi && fi.iva_breakdown && (
                <div className="mt-3 pt-3 border-t border-slate-200 text-left text-xs text-slate-500 space-y-1">
                  {fi.iva_breakdown.map((b, i) => (
                    <div key={i} className="flex justify-between">
                      <span>Base ({b.rate}%)</span>
                      <span>€{b.base.toFixed(2)} + €{b.cuota.toFixed(2)} IVA</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  const printWindow = window.open('', '_blank', 'width=320,height=700');
                  const receiptEl = document.getElementById('receipt-hidden');
                  if (printWindow && receiptEl) {
                    printWindow.document.write('<html><head><title>Receipt</title><style>@page{margin:0}body{margin:8px;font-family:monospace;font-size:12px}</style></head><body>');
                    printWindow.document.write(receiptEl.innerHTML);
                    printWindow.document.write('</body></html>');
                    printWindow.document.close();
                    printWindow.focus();
                    setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
                  }
                }}
                className="vf-btn-secondary flex-1 flex items-center justify-center gap-2"
                data-testid="pos-print-receipt"
              >
                <Printer className="w-5 h-5" />
                {t('pos_print_receipt')}
              </button>
              <button
                onClick={handleNewSale}
                className="vf-btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="pos-new-sale"
              >
                {t('pos_new_sale')}
              </button>
            </div>
          </motion.div>

          <div id="receipt-hidden" style={{ position: 'absolute', left: '-9999px', top: 0 }}>
            <ReceiptPrint sale={lastSale} shop={shop} />
          </div>
        </div>
      </ShopLayout>
    );
  }

  return (
    <div className="h-screen bg-[#F8FAFC] flex flex-col" data-testid="pos-page">
      {/* Offline / Sync Banner */}
      <AnimatePresence>
        {!isOnline && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-amber-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium"
            data-testid="offline-banner"
          >
            <WifiOff className="w-4 h-4" />
            {t('pos_offline_mode')} — {t('pos_offline_sales_saved')}
          </motion.div>
        )}
        {isOnline && wasOffline && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-emerald-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium"
            data-testid="back-online-banner"
          >
            <Wifi className="w-4 h-4" />
            {t('pos_back_online')}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sync result toast */}
      <AnimatePresence>
        {syncResult && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 ${
              syncResult.failed > 0 ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
            }`}
            data-testid="sync-result-toast"
          >
            {syncResult.failed > 0 ? (
              <><AlertTriangle className="w-4 h-4" /> {t('pos_sync_partial')}: {syncResult.synced} {t('pos_synced')}, {syncResult.failed} {t('pos_failed')}</>
            ) : (
              <><CheckCircle2 className="w-4 h-4" /> {syncResult.synced} {t('pos_sales_synced')}</>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-[#1e3a5f] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/shop/dashboard')}
            className="p-2 hover:bg-white/10 rounded-sm"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-bold text-lg">{t('pos_title')}</h1>
            <p className="text-white/60 text-sm">{shop?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Offline indicator */}
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-medium ${
            isOnline ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'
          }`} data-testid="connection-status">
            {isOnline ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {isOnline ? t('pos_online') : t('pos_offline')}
          </div>

          {/* Pending sync badge */}
          {pendingCount > 0 && (
            <button
              onClick={handleSync}
              disabled={syncing || !isOnline}
              className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-medium bg-orange-500/20 text-orange-300 hover:bg-orange-500/30 disabled:opacity-50"
              data-testid="sync-btn"
              title={`${pendingCount} pending sales`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? t('pos_syncing') : `${pendingCount} ${t('pos_pending')}`}
            </button>
          )}

          {usingCache && (
            <div className="flex items-center gap-1 px-2 py-1.5 rounded-sm text-xs bg-slate-500/20 text-slate-300" data-testid="cache-badge">
              <CloudOff className="w-3.5 h-3.5" />
              {t('pos_cached')}
            </div>
          )}

          <button className="p-2 hover:bg-white/10 rounded-sm" title={t('pos_park')}>
            <ParkingCircle className="w-5 h-5" />
          </button>
          <button
            onClick={clearCart}
            className="p-2 hover:bg-white/10 rounded-sm text-red-400"
            title={t('pos_void')}
          >
            <RotateCcw className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Products Panel */}
        <div className="flex-1 flex flex-col p-4 overflow-hidden">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('pos_search')}
              className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
              data-testid="pos-search"
            />
            <button
              onClick={() => setShowCameraScanner(!showCameraScanner)}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-sm transition-colors ${
                showCameraScanner ? 'bg-[#1e3a5f] text-white' : 'bg-slate-100 text-slate-600'
              }`}
              data-testid="pos-camera-scan-btn"
            >
              <Scan className="w-5 h-5" />
            </button>
          </div>

          {/* Camera Scanner */}
          {showCameraScanner && (
            <div className="mb-4 max-w-sm" data-testid="pos-camera-scanner-wrapper">
              <CameraBarcodeScanner
                onScan={handleCameraScan}
                onClose={() => setShowCameraScanner(false)}
              />
            </div>
          )}

          {/* Categories */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2 flex-shrink-0">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-sm whitespace-nowrap transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-[#1e3a5f] text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              {t('pos_all')}
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-sm whitespace-nowrap transition-colors ${
                  selectedCategory === cat
                    ? 'bg-[#1e3a5f] text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Product Grid */}
          <div className="flex-1 overflow-y-auto">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">{t('pos_no_products')}</p>
                <p className="text-sm text-slate-400">{t('pos_add_first')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {filteredProducts.map(product => (
                  <button
                    key={product.id}
                    onClick={() => addToCart(product)}
                    className="bg-white border-2 border-slate-100 p-4 text-left hover:border-[#22c55e] hover:shadow-md transition-all group"
                    data-testid={`pos-product-${product.id}`}
                  >
                    <div className="aspect-square bg-slate-50 rounded-sm mb-3 flex items-center justify-center overflow-hidden">
                      {product.image_url ? (
                        <img src={product.image_url.startsWith('http') ? product.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${product.image_url}`}
                          alt={product.name} className="w-full h-full object-contain p-2"
                          onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span class="text-3xl">📦</span>'; }} />
                      ) : (
                        <span className="text-3xl">📦</span>
                      )}
                    </div>
                    <p className="font-medium text-[#1e3a5f] text-sm truncate group-hover:text-[#22c55e]">
                      {product.name}
                    </p>
                    <p className="text-lg font-bold text-[#22c55e]">€{product.price.toFixed(2)}</p>
                    {product.stock_count !== null && product.stock_count <= product.low_stock_threshold && (
                      <p className="text-xs text-amber-600 mt-1">Stock: {product.stock_count}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cart Panel */}
        <div className="w-96 bg-white border-l-2 border-slate-200 flex flex-col">
          {/* Cart Header */}
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-[#1e3a5f] flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                {t('pos_cart')}
              </h2>
              <span className="text-sm text-slate-500">
                {cart.reduce((sum, item) => sum + item.quantity, 0)} {t('pos_items')}
              </span>
            </div>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {cart.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-slate-200" />
                <p className="text-slate-400">{t('pos_empty')}</p>
                <p className="text-sm text-slate-300">{t('pos_add_products')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map(item => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="bg-slate-50 p-3 rounded-sm"
                  >
                    <div className="flex justify-between mb-2">
                      <p className="font-medium text-[#1e3a5f] text-sm">{item.name}</p>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="w-8 h-8 bg-white border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-100"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="w-8 h-8 bg-white border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-100"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="font-bold text-[#1e3a5f]">
                        €{(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Cart Footer */}
          <div className="p-4 border-t-2 border-slate-200 bg-slate-50">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('pos_subtotal')}</span>
                <span>€{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('pos_tax')}</span>
                <span>€{taxTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xl font-bold">
                <span className="text-[#1e3a5f]">{t('pos_total')}</span>
                <span className="text-[#22c55e]">€{total.toFixed(2)}</span>
              </div>
            </div>

            <button
              onClick={() => setShowPayment(true)}
              disabled={cart.length === 0}
              className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="pos-pay-btn"
            >
              <Calculator className="w-5 h-5" />
              {t('pos_pay')} €{total.toFixed(2)}
            </button>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPayment && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              onClick={() => setShowPayment(false)}
            >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full md:w-[500px] bg-white rounded-sm flex flex-col max-h-[90vh]"
              data-testid="payment-modal"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xl font-bold text-[#1e3a5f]">{t('pos_pay')}</h3>
                <div className="flex items-center gap-2">
                  {!isOnline && (
                    <span className="flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-sm">
                      <WifiOff className="w-3 h-3" /> {t('pos_offline')}
                    </span>
                  )}
                  <button
                    onClick={() => setShowPayment(false)}
                    className="p-2 hover:bg-slate-100 rounded-sm"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Total */}
                <div className="text-center py-4 bg-slate-50 rounded-sm">
                  <p className="text-slate-600 mb-1">{t('pos_total')}</p>
                  <p className="text-4xl font-bold text-[#22c55e]">€{total.toFixed(2)}</p>
                </div>

                {/* Payment Method */}
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    className={`p-4 border-2 rounded-sm flex flex-col items-center gap-2 transition-colors ${
                      paymentMethod === 'cash' ? 'border-[#22c55e] bg-green-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="payment-cash"
                  >
                    <Banknote className="w-6 h-6" />
                    <span className="text-sm font-medium">{t('pos_cash')}</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('card')}
                    className={`p-4 border-2 rounded-sm flex flex-col items-center gap-2 transition-colors ${
                      paymentMethod === 'card' ? 'border-[#22c55e] bg-green-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="payment-card"
                  >
                    <CreditCard className="w-6 h-6" />
                    <span className="text-sm font-medium">{t('pos_card')}</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('split')}
                    className={`p-4 border-2 rounded-sm flex flex-col items-center gap-2 transition-colors ${
                      paymentMethod === 'split' ? 'border-[#22c55e] bg-green-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="payment-split"
                  >
                    <Split className="w-6 h-6" />
                    <span className="text-sm font-medium">{t('pos_split')}</span>
                  </button>
                </div>

                {/* Cash Payment Options */}
                {paymentMethod === 'cash' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                        {t('pos_cash_received')}
                      </label>
                      <input
                        type="number"
                        value={cashReceived}
                        onChange={(e) => setCashReceived(e.target.value)}
                        placeholder="0.00"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-sm text-2xl text-center font-bold focus:border-[#1e3a5f] focus:outline-none"
                        data-testid="cash-input"
                      />
                    </div>

                    <div className="grid grid-cols-5 gap-2">
                      <button
                        onClick={() => setCashReceived(total.toFixed(2))}
                        className="p-3 bg-[#22c55e] text-white rounded-sm font-medium hover:bg-[#16a34a]"
                      >
                        {t('pos_exact')}
                      </button>
                      {quickCashAmounts.map(amount => (
                        <button
                          key={amount}
                          onClick={() => setCashReceived(amount.toString())}
                          className="p-3 bg-slate-100 rounded-sm font-medium hover:bg-slate-200"
                        >
                          €{amount}
                        </button>
                      ))}
                    </div>

                    {parseFloat(cashReceived) > total && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-green-700">{t('pos_change')}</span>
                          <span className="text-2xl font-bold text-green-700">
                            €{(parseFloat(cashReceived) - total).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Customer Name */}
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                    {t('pos_customer_name')}
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-slate-200 flex gap-4">
                <button
                  onClick={() => setShowPayment(false)}
                  className="vf-btn-secondary flex-1"
                >
                  {t('pos_back')}
                </button>
                <button
                  onClick={handlePayment}
                  disabled={processing || (paymentMethod === 'cash' && (!cashReceived || parseFloat(cashReceived) < total))}
                  className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="complete-sale-btn"
                >
                  {processing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      {!isOnline && <CloudOff className="w-4 h-4" />}
                      {t('pos_complete_sale')}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default POS;
